import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box, Typography } from "@mui/material";
import EasyAnchorLink from "../../UI/AnchorLink";

import { GridColDef } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import KuebikoQueryCell from "../UI/KuebikoQueryCell";

import "../BrandMetadataReview.css";
import PlacesDrillDownModal from "../UI/PlacesDrillDownModal";

interface PlacesOmniReviewProps {
  omni_key: string;
  country_code: string;
}

const ReviewPlacesOmniNames: React.FC<PlacesOmniReviewProps> = ({
  omni_key,
  country_code,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();

  const columns_def: GridColDef[] = [
    {
      field: "chain_id",
      headerName: "Chain ID",
      type: "string",
      width: 150,
    },
    {
      field: "external_id",
      headerName: "External ID",
      type: "string",
      width: 150,
    },
    {
      field: "omni_key",
      headerName: "Omni Key",
      type: "string",
      width: 150,
    },
    {
      field: "metadata_brand_name",
      headerName: "Metadata Brand Name",
      description: "The brand name as listed in the metadata.",
      type: "string",
      width: 250,
    },
    {
      field: "actual_place_name",
      headerName: "Actual Place Name",
      description: "The actual name of the place as found in the places table.",
      type: "string",
      width: 250,
    },
    {
      field: "place_name_count",
      headerName: "Place Name Count",
      description:
        "The count of occurrences of the place name in the places table.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "kuebiko_query",
      headerName: "Kuebiko Query",
      description:
        "Copy or view the Kuebiko query used to review places for retraction.",
      filterable: false,
      sortable: false,
      width: 250,
      renderCell: (params) => {
        const queryTemplate = `
        SELECT id, name, country_code, business_category_id, address, chain_id, website,
            external_ids -> 'EXTERNAL_ID' AS "omni_id"
        FROM "places"
        WHERE external_ids -> 'EXTERNAL_ID' IS NOT NULL
        AND country_code = 'COUNTRY_CODE'
        AND name = 'ACTUAL_PLACE_NAME'
        `;

        const columnMappings = {
          EXTERNAL_ID: params.row.external_id,
          COUNTRY_CODE: country_code,
          ACTUAL_PLACE_NAME: params.row.actual_place_name,
        };

        return (
          <KuebikoQueryCell
            queryTemplate={queryTemplate}
            columnMappings={columnMappings}
            tooltip="Kuebiko Query"
          />
        );
      },
    },
    {
      field: "drill_down",
      headerName: "View Places",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const api = "/brand_places/get_brand_places_by_omni_biz_name";
        const apiParams = {
          omni_key: omni_key,
          country_code: country_code,
          actual_place_name: params.row.actual_place_name,
        };

        return (
          <>
            <PlacesDrillDownModal api={api} apiParams={apiParams} />
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (omni_key && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        omni_key: omni_key,
        country_code: country_code,
      });

      const fetchBusinessNames = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/places_name_review_by_country?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch places business names to review for omni ${omni_key} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBusinessNames();
    } else {
      setData([]);
    }
  }, [omni_key, country_code]);

  return (
    <Box className="metadata-review-container">
      <Box display="flex" alignItems="center" id="business-names">
        <EasyAnchorLink id="business-names" />
        <Typography variant="h6" gutterBottom sx={{ fontSize: "1.25rem" }}>
          Business Names
        </Typography>
      </Box>

      <GaneshDataGrid
        gridId="placesReviewOmniNames"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewPlacesOmniNames;
